import { ChangeDetectionStrategy, Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Observable, filter, startWith, map } from 'rxjs';
import { MenuItem } from 'primeng/api';

import { UserService } from '@ebf/core/user/services/user.service';
import { UserDto } from '@ebf/features/users/domain';
import { EventsApiService } from '@ebf/shared/events/services/events-api.service';
import { getUserFullName } from '@ebf/features/users/utils';
import { AutoUnsubscribable } from '@ebf-libs/sdk';

@Component({
  selector: 'ebf-layout-app',
  templateUrl: './layout-app.component.html',
  styleUrls: ['./layout-app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutAppComponent extends AutoUnsubscribable implements OnInit, OnDestroy {
  public menuItems: MenuItem[];
  public userMenuItems: MenuItem[];
  public selectedItem: MenuItem;
  public currentYear: number;
  public isMobileMenuOpen: boolean;
  public user: UserDto;
  public role$: Observable<string>;

  constructor(
    public readonly userService: UserService,
    private readonly eventsApiService: EventsApiService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.user = this.userService.getUserSnapshot();
    this.currentYear = new Date().getFullYear();
    this.eventsApiService.connect();
    this.setupMenuItems();
    this.selectedItem = this.menuItems[0];
    this.setupRole$();

    this.subSink.sink = this.userService.user$.subscribe(() => {
      this.user = this.userService.getUserSnapshot();
      this.setupMenuItems();
    });
  }

  private setupMenuItems(): void {
    this.userMenuItems = this.userService.isImpersonated()
      ? this.getImpersonatedMenuItems()
      : this.getNormalMenuItems();

    this.menuItems = [
      { label: 'Championships', routerLink: '/championships' },
      { label: 'Users', routerLink: '/users', visible: this.userService.isAdmin() },
      { label: 'Requests', routerLink: '/requests', visible: this.isRequestPageVisible() },
      // { label: 'Help', routerLink: '/faq' },
    ];
  }

  private getImpersonatedMenuItems(): MenuItem[] {
    return [
      {
        label: `You: ${getUserFullName(this.user.impersonatedBy)}`,
        disabled: true,
        styleClass: 'font-weight-bold',
      },
      {
        label: this.user.impersonatedBy?.email,
        disabled: true,
        styleClass: 'admin-email',
      },
      {
        separator: true,
      },
      {
        label: 'End impersonation',
        styleClass: 'text-danger',
        command: () => {
          this.subSink.sink = this.userService.endImpersonation().subscribe({
            next: () => {
              window.location.reload();
            },
          });
        },
      },
    ];
  }

  private getNormalMenuItems(): MenuItem[] {
    return [
      {
        label: 'Logout',
        command: () => {
          this.eventsApiService.disconnect();
          this.userService.logout();
        },
      },
    ];
  }

  private setupRole$(): void {
    this.role$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(null),
      map(() => {
        const activatedChildRoute: ActivatedRouteSnapshot = this.getActivatedChildRoute(
          this.activatedRoute.snapshot,
        );
        const { id, countryId } = activatedChildRoute?.params || {};
        const { countryUuid } = activatedChildRoute?.queryParams || {};
        const targetChampionshipId: string = countryUuid || countryId || id;

        if (this.userService.isGuest()) {
          return null;
        }

        if (this.userService.isAdmin()) {
          return 'Admin';
        }

        if (this.userService.isResponsible(targetChampionshipId)) {
          return 'Responsible';
        }

        if (this.userService.isEventManager(targetChampionshipId)) {
          return 'Event Manager';
        }

        if (this.userService.isContentManager(targetChampionshipId)) {
          return 'Content Manager';
        }

        if (this.userService.isJudge(targetChampionshipId)) {
          return 'Judge';
        }

        if (this.userService.isModel(targetChampionshipId)) {
          return 'Model';
        }

        return 'User';
      }),
    );
  }

  private isRequestPageVisible(): boolean {
    return ['ADMIN', 'RESPONSIBLE'].includes(this.userService.getUserSnapshot()?.role);
  }

  public getUsername(): string {
    const username = getUserFullName(this.user);
    const maxLength = 21;
    const currentLength = username.length;

    return currentLength > maxLength ? `${username.slice(0, maxLength)}...` : username;
  }

  public onToggleMobile(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  private getActivatedChildRoute(activatedRouteSnapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    return activatedRouteSnapshot.firstChild
      ? this.getActivatedChildRoute(activatedRouteSnapshot.firstChild)
      : activatedRouteSnapshot;
  }
}
